import { useState, useEffect, useRef } from "react";
import { get, set, del, keys } from "idb-keyval";
import { getKiinteistoSasToken, getKiinteistoImagesByKiinteistoId, getKiinteistoImageById } from "../../api/api";
import { useRakennusData } from "../../context/RakennusDataContext";
const azureBase = process.env.REACT_APP_AZURE_BLOB_BASE;

async function convertIfHeic(blob) {
  const type = blob.type?.toLowerCase() ?? '';
  const isHeic = type === 'image/heic' || type === 'image/heif' || type === 'image/heic-sequence';
  if (!isHeic) return blob;
  try {
    const heic2any = (await import('heic2any')).default;
    const converted = await heic2any({ blob, toType: 'image/jpeg', quality: 0.92 });
    return Array.isArray(converted) ? converted[0] : converted;
  } catch (err) {
    console.warn('[ImageHandler] HEIC conversion failed, using original blob:', err);
    return blob;
  }
}

const defaultDimension = 800;
const defaultQuality = 0.35;

// ─── Module-level caches (shared across all hook instances) ──────────────────

const MEMORY_LIMIT = 150;
const memoryCache = new Map();

function setMemoryCache(id, blobUrl) {
  if (memoryCache.has(id)) memoryCache.delete(id);
  memoryCache.set(id, blobUrl);
  while (memoryCache.size > MEMORY_LIMIT) {
    const oldestId = memoryCache.keys().next().value;
    URL.revokeObjectURL(memoryCache.get(oldestId));
    memoryCache.delete(oldestId);
  }
}

// Metadata API response cache: `${propertyId}|${category}|${subCategory}` → { rows, ts }
const metadataCache = new Map();
const METADATA_TTL = 60_000; // 60 seconds

export function invalidateMetadataCache(propertyId) {
  for (const key of metadataCache.keys()) {
    if (key.startsWith(`${propertyId}|`)) metadataCache.delete(key);
  }
}

// Event bus: emits 'progress' whenever a pdf-final key is written
export const pdfCacheEvents = new EventTarget();

// ─── Web Worker pool for off-thread canvas compression ───────────────────────

const workerPool = Array.from({ length: 2 }, () =>
  new Worker(new URL("../../workers/imageCompressor.worker.js", import.meta.url))
);
let workerPoolIdx = 0;
const workerPending = new Map(); // id → { resolve, reject }

workerPool.forEach(worker => {
  worker.onmessage = ({ data: { id, arrayBuffer, ok, error } }) => {
    const pending = workerPending.get(id);
    if (!pending) return;
    workerPending.delete(id);
    if (ok) {
      const bytes = new Uint8Array(arrayBuffer);
      const chunkSize = 65536;
      const chunks = [];
      for (let i = 0; i < bytes.length; i += chunkSize) {
        chunks.push(String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize)));
      }
      pending.resolve("data:image/jpeg;base64," + btoa(chunks.join("")));
    } else {
      pending.reject(new Error(error));
    }
  };
  worker.onerror = err => console.error("[ImageWorker] Error:", err);
});

async function applyRotationResizeCompression(blob, rotation = 0, dimensions = defaultDimension, quality = defaultQuality) {
  const id = crypto.randomUUID();
  const mimeType = blob.type || "image/jpeg";
  const arrayBuffer = await blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    workerPending.set(id, { resolve, reject });
    const worker = workerPool[workerPoolIdx++ % workerPool.length];
    worker.postMessage({ id, arrayBuffer, mimeType, rotation, dimensions, quality }, [arrayBuffer]);
  });
}

// ─── Key helpers ─────────────────────────────────────────────────────────────

export const getPtsKey = (propertyId, category, imageId) =>
  `pts-${propertyId}-${category}-${imageId}`;

const getRawKey = (imageId) => `raw-${imageId}`;

// PDF processed cache (kept for compatibility, not actively used)
const getPdfKey = (imageId, rotation = 0) =>
  `pdf-${imageId}-${rotation}`;

// ─── Utilities ───────────────────────────────────────────────────────────────

export const isImageInDB = async (imageId) => {
  try {
    const resp = await getKiinteistoImageById(imageId);
    return !!resp.data;
  } catch (err) {
    if (err.response?.status === 404) return false;
    console.error("Failed to verify image in DB:", err);
    return false;
  }
};

export const preloadPtsImages = async (blocks = [], propertyId, ptsImageUrls, setPtsImageUrls) => {
  if (!Array.isArray(blocks)) return;
  for (const block of blocks) {
    if (block?.data?.imageRef) {
      await fetchPtsImageWithCache(block.data.imageRef, propertyId, ptsImageUrls, setPtsImageUrls);
    }
    if (block.type === "subsection" && Array.isArray(block.data?.blocks)) {
      await preloadPtsImages(block.data.blocks, propertyId, ptsImageUrls, setPtsImageUrls);
    }
  }
};

// ─── PDF image compression ───────────────────────────────────────────────────

// Pre-compress PDF-ready image (rotation + resize + quality) and cache
export async function precompressPdfImage(
  img,
  { propertyId, dimensions = defaultDimension, quality = defaultQuality } = {}
) {
  if (!img?.id || !propertyId) return null;

  const rotation = img.metadata?.rotation || 0;
  const pdfFinalKey = `pdf-final-${propertyId}-${img.id}-${rotation}-${dimensions}-${quality}`;

  // Return cached PDF if exists
  try {
    const cached = await get(pdfFinalKey);
    if (cached) return cached;
  } catch (err) {
    console.warn(`[PDF] Failed to get cached PDF:`, err);
  }

  // Fetch RAW blob
  let blob = await get(getRawKey(img.id));
  if (!blob) {
    try {
      const sasRes = await getKiinteistoSasToken(img.azure_blob_name);
      const response = await fetch(sasRes.data.sasUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      blob = await response.blob();
      await set(getRawKey(img.id), blob);
    } catch (err) {
      console.error(`[PDF] Failed to fetch RAW image ${img.id}:`, err);
      return null;
    }
  }

  blob = await convertIfHeic(blob);

  // Rotate + resize + compress (off main thread via worker)
  let base64;
  try {
    base64 = await applyRotationResizeCompression(blob, rotation, dimensions, quality);
    await set(pdfFinalKey, base64);
    pdfCacheEvents.dispatchEvent(new CustomEvent("progress", { detail: { propertyId, imageId: img.id } }));
  } catch (err) {
    console.error(`[PDF] Failed to compress image ${img.id}:`, err);
    return null;
  }

  return base64;
}

// Remove stale pdf-final keys for a property (one scan per export, not per image)
export async function cleanStalePdfKeys(propertyId) {
  try {
    const allKeys = await keys();
    const prefix = `pdf-final-${propertyId}-`;
    const byImageId = new Map(); // imageId → [key, ...]

    for (const key of allKeys) {
      if (typeof key !== "string" || !key.startsWith(prefix)) continue;
      const rest = key.slice(prefix.length);
      const imageId = rest.split("-")[0];
      if (!byImageId.has(imageId)) byImageId.set(imageId, []);
      byImageId.get(imageId).push(key);
    }

    for (const keyList of byImageId.values()) {
      if (keyList.length <= 1) continue;
      // Keep the last key in the list, delete the rest
      for (let i = 0; i < keyList.length - 1; i++) {
        await del(keyList[i]);
      }
    }
  } catch (err) {
    console.warn("[PDF] cleanStalePdfKeys failed:", err);
  }
}

// ─── Image fetchers ───────────────────────────────────────────────────────────

export async function fetchImageAsBase64(img, report) {
  if (!img) return null;

  const propertyId =
    report.property_id ||
    report.kiinteisto_id ||
    img.id_kiinteisto ||
    img.metadata?.id_kiinteisto ||
    (img.azure_blob_name ? img.azure_blob_name.split("/")[0] : "unknown");

  const category = img.metadata?.category;
  const cacheKey = `pts-${propertyId}-${category}`;

  try {
    const cachedBlob = await get(cacheKey);
    if (cachedBlob) return await blobToDataUrl(cachedBlob);
  } catch (err) {
    console.error("Error checking local cache:", err);
  }

  try {
    let imageUrl = `${azureBase}/${img.azure_blob_name}`;
    if (!img.sasUrl && typeof getKiinteistoSasToken === "function") {
      const res = await getKiinteistoSasToken(img.azure_blob_name);
      img.sasUrl = res?.data?.sasUrl;
      imageUrl = img.sasUrl || imageUrl;
    }

    const response = await fetch(imageUrl);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await blobToDataUrl(blob);
  } catch (err) {
    console.error("Error fetching image:", err);
    return null;
  }
}

export const renderPtsImage = (block, kiinteistoId, ptsImageUrls) => {
  if (!block.data?.imageRef || !kiinteistoId) return null;
  const { id, metadata } = block.data.imageRef;
  const cacheKey = getPtsKey(kiinteistoId, metadata.category, id);
  const blobUrl = ptsImageUrls[cacheKey];
  if (!blobUrl) return null;

  const openInNewWindow = () => {
    window.open(blobUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="mb-1 text-center">
      <small className="text-muted d-block mb-1" style={{ fontSize: "0.7rem" }}>
        {metadata.category}
      </small>
      <img
        src={blobUrl}
        alt={metadata.description || ""}
        onClick={openInNewWindow}
        style={{
          width: "80%",
          maxHeight: 180,
          objectFit: "contain",
          borderRadius: 4,
          cursor: "pointer",
          transition: "transform 0.2s, box-shadow 0.2s",
        }}
        onMouseEnter={e => {
          e.currentTarget.style.transform = "scale(1.05)";
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.25)";
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = "scale(1)";
          e.currentTarget.style.boxShadow = "none";
        }}
      />
    </div>
  );
};

async function processPdfImage(blob, rotation) {
  const bitmap = await createImageBitmap(blob);

  const swap = rotation % 180 !== 0;

  const canvas = document.createElement("canvas");
  canvas.width = swap ? bitmap.height : bitmap.width;
  canvas.height = swap ? bitmap.width : bitmap.height;

  const ctx = canvas.getContext("2d");
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.drawImage(bitmap, -bitmap.width / 2, -bitmap.height / 2);

  return canvas.toDataURL("image/jpeg", 0.9);
}

export const fetchPtsImageWithCache = async (
  imgRef,
  propertyId,
  ptsImageUrls,
  setPtsImageUrls,
) => {
  if (!imgRef?.id || !propertyId) return null;

  const { id, azure_blob_name, metadata } = imgRef;

  const stateKey = getPtsKey(propertyId, metadata.category, id);
  const rawKey = getRawKey(id);

  const existsInDB = await isImageInDB(id);

  if (!existsInDB) {
    await set(rawKey, undefined);
    if (ptsImageUrls[stateKey]) {
      URL.revokeObjectURL(ptsImageUrls[stateKey]);
    }
    setPtsImageUrls(prev => ({ ...prev, [stateKey]: undefined }));
    return null;
  }

  let blob = await get(rawKey);

  if (!blob) {
    try {
      const res = await getKiinteistoSasToken(azure_blob_name);
      const response = await fetch(res.data.sasUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      blob = await response.blob();
      await set(rawKey, blob);
    } catch (err) {
      console.error(`[PTS] Failed to fetch ${azure_blob_name}:`, err);
      return null;
    }
  }

  const blobUrl = URL.createObjectURL(blob);
  setPtsImageUrls(prev => ({ ...prev, [stateKey]: blobUrl }));

  precompressPdfImage(imgRef, { propertyId, dimensions: defaultDimension, quality: defaultQuality }).catch(console.error);

  return blobUrl;
};

// Simple image fetcher without compression
export async function fetchImages(
  propertyId,
  { category = null, subCategory = null, asBase64 = false } = {}
) {
  const availableImages = [];
  const imageUrls = {};

  const res = await getKiinteistoImagesByKiinteistoId(propertyId, {
    category,
    subCategory,
    requestPageNumber: 1,
    fetchLimit: 9999,
  });

  const rows = res.data?.rows || [];

  for (const img of rows) {
    availableImages.push(img);

    let blob = await get(getRawKey(img.id));

    if (!blob) {
      const sasRes = await getKiinteistoSasToken(img.azure_blob_name);
      const response = await fetch(sasRes.data.sasUrl);
      blob = await response.blob();
      await set(getRawKey(img.id), blob);
    }

    const displayBlob = await convertIfHeic(blob);
    imageUrls[img.id] = asBase64
      ? await blobToDataUrl(displayBlob)
      : URL.createObjectURL(displayBlob);
  }

  return { availableImages, imageUrls };
}

async function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export async function applyRotationFromMetadata(blob, rotation) {
  if (!rotation || rotation % 360 === 0) return blob;

  const bitmap = await createImageBitmap(blob);
  const swap = rotation % 180 !== 0;
  const canvas = document.createElement("canvas");
  canvas.width = swap ? bitmap.height : bitmap.width;
  canvas.height = swap ? bitmap.width : bitmap.height;

  const ctx = canvas.getContext("2d");
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.drawImage(bitmap, -bitmap.width / 2, -bitmap.height / 2);

  return new Promise(resolve => {
    canvas.toBlob(b => resolve(b), "image/jpeg");
  });
}

// PDF image fetcher: returns { availableImages, imageUrls } with base64-encoded compressed images
export async function fetchImagesForPDF(
  propertyId,
  { category = null, subCategory = null, dimensions = defaultDimension, quality = defaultQuality } = {}
) {
  const availableImages = [];
  const imageUrls = {};

  const metaKey = `${propertyId}|${category ?? ""}|${subCategory ?? ""}`;
  let rows;
  const cached = metadataCache.get(metaKey);
  if (cached && Date.now() - cached.ts < METADATA_TTL) {
    rows = cached.rows;
  } else {
    const res = await getKiinteistoImagesByKiinteistoId(propertyId, {
      category,
      subCategory,
      requestPageNumber: 1,
      fetchLimit: 9999,
    });
    rows = res.data?.rows || [];
    metadataCache.set(metaKey, { rows, ts: Date.now() });
  }

  for (const img of rows) {
    availableImages.push(img);
    try {
      const processedBase64 = await precompressPdfImage(img, { propertyId, dimensions, quality });
      imageUrls[img.id] = processedBase64;
    } catch (err) {
      console.error(`[PDF] Failed to process image ${img.id}:`, err);
    }
  }

  // One cleanup pass per export (not per image)
  cleanStalePdfKeys(propertyId).catch(console.warn);

  return { availableImages, imageUrls };
}

// ─── React hook ───────────────────────────────────────────────────────────────

export function useImageHandler(propertyId, { category = null, subCategory = null } = {}) {
  const [availableImages, setAvailableImages] = useState([]);
  const [imageUrls, setImageUrls] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const loadedOnce = useRef(false);

  const fetchSasUrlWithCache = async (img, forceRefetch = false) => {
    if (!img?.id) return null;

    // Return cached ObjectURL from memory LRU
    if (!forceRefetch && memoryCache.has(img.id)) {
      const cached = memoryCache.get(img.id);
      setImageUrls(prev => ({ ...prev, [img.id]: cached }));
      return cached;
    }

    // Try RAW cache from IndexedDB — skipped on forceRefetch. Callers pass
    // that when recovering from a load failure (an <img onError>), and the
    // cached raw bytes are exactly what's most likely to actually be bad
    // (see the response.ok check below) — re-reading the same cached blob
    // would just reproduce the same broken image forever, which is why a
    // plain page refresh alone didn't fix it before (IndexedDB survives a
    // reload; the in-memory blob-URL cache doesn't).
    let blob = forceRefetch ? null : await get(getRawKey(img.id));
    if (!blob) {
      try {
        const res = await getKiinteistoSasToken(img.azure_blob_name);
        const response = await fetch(res.data.sasUrl);
        if (!response.ok) throw new Error(`SAS blob fetch failed (${response.status})`);
        blob = await response.blob();
        await set(getRawKey(img.id), blob);
      } catch (err) {
        console.error("Failed to fetch SAS URL:", err);
        return `${azureBase}/${img.azure_blob_name}`;
      }
    }

    const blobUrl = URL.createObjectURL(blob);
    setMemoryCache(img.id, blobUrl);
    setImageUrls(prev => ({ ...prev, [img.id]: blobUrl }));

    // Fire-and-forget PDF precompress
    precompressPdfImage(img, { propertyId, dimensions: defaultDimension, quality: defaultQuality }).catch(console.error);

    return blobUrl;
  };

  const loadImages = async (forceRefetch = false) => {
    if (!propertyId) return;
    setIsLoading(true);
    try {
      const metaKey = `${propertyId}|${category ?? ""}|${subCategory ?? ""}`;
      let imgs;

      if (!forceRefetch) {
        const cached = metadataCache.get(metaKey);
        if (cached && Date.now() - cached.ts < METADATA_TTL) {
          imgs = cached.rows;
        }
      } else {
        // Bust all cache entries for this property so sibling views (e.g. "Kaikki")
        // also reflect the upload without needing a page refresh.
        for (const key of metadataCache.keys()) {
          if (key.startsWith(`${propertyId}|`)) metadataCache.delete(key);
        }
      }

      if (!imgs) {
        const res = await getKiinteistoImagesByKiinteistoId(propertyId, { category, subCategory, fetchLimit: 9999 });
        imgs = res.data?.rows || [];
        metadataCache.set(metaKey, { rows: imgs, ts: Date.now() });
      }

      setAvailableImages(imgs);
      if (forceRefetch) setImageUrls({});

      // Fetch URLs in parallel batches of 4
      const CONCURRENCY = 4;
      for (let i = 0; i < imgs.length; i += CONCURRENCY) {
        await Promise.all(imgs.slice(i, i + CONCURRENCY).map(img => fetchSasUrlWithCache(img)));
      }
    } catch (err) {
      console.error("Failed to load images:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!loadedOnce.current && propertyId) {
      loadImages();
      loadedOnce.current = true;
    }
  }, [propertyId]);

  return { availableImages, imageUrls, isLoading, fetchSasUrlWithCache, setAvailableImages, loadImages };
}

// ─── Property-wide eager precompression ──────────────────────────────────────

// Call once on report open (fire-and-forget) to warm the PDF cache for all images.
export async function precompressAllImagesForProperty(propertyId) {
  if (!propertyId) return;
  try {
    const metaKey = `${propertyId}||`;
    let imgs;
    const cached = metadataCache.get(metaKey);
    if (cached && Date.now() - cached.ts < METADATA_TTL) {
      imgs = cached.rows;
    } else {
      const res = await getKiinteistoImagesByKiinteistoId(propertyId, { fetchLimit: 9999 });
      imgs = res.data?.rows || [];
      metadataCache.set(metaKey, { rows: imgs, ts: Date.now() });
    }

    const CONCURRENCY = 2;
    for (let i = 0; i < imgs.length; i += CONCURRENCY) {
      await Promise.all(
        imgs.slice(i, i + CONCURRENCY).map(img =>
          precompressPdfImage(img, { propertyId, dimensions: defaultDimension, quality: defaultQuality }).catch(console.warn)
        )
      );
    }
  } catch (err) {
    console.error("[precompressAll]", err);
  }
}
